import { useState, useRef, forwardRef, useImperativeHandle, useCallback, useMemo } from "react";
import { ColorPicker, ColorPickerRef } from "./colorsPicker";
import { SizePicker, SizePickerRef } from "./SizePicker";
import { ColorSizeEntry, Variations } from "@utilities/types";
import { Input } from "./Input";

export interface ColorSizePickerRef {
  resetColorSize: () => void;
}

interface ColorSizePickerProps {
  initialVariation?: Variations | null;
  onChange: (colorSizeList: ColorSizeEntry[]) => void;
}

export const ColorSizePicker = forwardRef<ColorSizePickerRef, ColorSizePickerProps>(
  ({ initialVariation, onChange }, ref) => {

    const colorPickerRef = useRef<ColorPickerRef>(null);
    const sizePickerRef = useRef<SizePickerRef>(null);

    const [colorSizeList, setColorSizeList] = useState<ColorSizeEntry[]>(
      // @ts-ignore
      initialVariation ? initialVariation.colorSizeData : []);
    const [selectedColor, setSelectedColor] = useState<string | null>(null);

    const colorsList = useMemo(()=> (
      colorSizeList.map(el=> ({ color: el.color })) || []
    ), [colorSizeList]);

    const sizesList = useMemo(()=> {
      if(!selectedColor) return [];
      const selectedEntry = colorSizeList.find(entry => entry.color === selectedColor);
      return selectedEntry ? selectedEntry.sizes.map(size => ({ size: size.size })) : [];
    }, [colorSizeList, selectedColor]);

    const handleColorChange = useCallback((colors: string[] | any) => {
      const updatedList = colors.map((color: string) => {
        const existingEntry = colorSizeList.find(entry => entry.color === color);
        return existingEntry || { color, sizes: [] };
      });
      setColorSizeList(updatedList);
      onChange(updatedList);
      setSelectedColor(null);
    }, [colorSizeList, onChange]);

    const handleSizeChange = useCallback((sizes: string[] | any) => {
      if (selectedColor) {
        const updatedList = colorSizeList.map(entry =>
          entry.color === selectedColor
            ? { ...entry, sizes: sizes.map((size: string) => ({ size, quantity: 0 })) }
            : entry
        );
        setColorSizeList(updatedList);
        onChange(updatedList);
      }
    }, [colorSizeList, selectedColor, onChange]);

    const handleQuantityChange = useCallback((color: string, size: string, quantity: number) => {
      const updatedList = colorSizeList.map(entry =>
        entry.color === color
          ? {
              ...entry,
              sizes: entry.sizes.map(s => (s.size === size ? { ...s, quantity } : s)),
            }
          : entry
      );
      setColorSizeList(updatedList);
      onChange(updatedList);
    }, [colorSizeList, onChange]);

    // ✅ Méthode reset appelée par le parent
    const resetColorSize = useCallback(() => {
      colorPickerRef.current?.resetColors();
      sizePickerRef.current?.resetSizes();
      setColorSizeList([]);
      setSelectedColor(null);
      onChange([]);
    }, [onChange]);

    useImperativeHandle(ref, () => ({
      resetColorSize
    }), [resetColorSize]);

    return (
      <div className="w-full p-4 border rounded-lg shadow-md bg-white">
        <h2 className="text-lg font-semibold text-gray-800 mb-3">Sélectionner une couleur et ses tailles</h2>

        <ColorPicker 
          ref={colorPickerRef} 
          initialColorsList={colorsList}
          onChange={handleColorChange} 
        />

        {colorSizeList.length > 0 && (
          <div className="mt-4">
            <label className="block text-gray-700 font-semibold mb-2">Sélectionnez une couleur</label>
            <div className="flex gap-2 flex-wrap">
              {colorSizeList.map((entry, index) => (
                <span
                  key={index}
                  onClick={() => setSelectedColor(entry.color)}
                  className={`w-8 h-8 rounded-full border border-gray-300 cursor-pointer ${
                    selectedColor === entry.color && "border-4 border-purple-400"
                  }`}
                  style={{ backgroundColor: entry.color }}
                ></span>
              ))}
            </div>
          </div>
        )}

        {selectedColor && (
          <div className="mt-4">
            <div className="flex items-center gap-2 mb-2">
              <p className="text-gray-700 font-semibold">Ajouter des tailles pour</p>
              <span
                className={`w-8 h-8 rounded-full border border-gray-300`}
                style={{ backgroundColor: selectedColor }}
              ></span>
            </div>
            <SizePicker 
              ref={sizePickerRef} 
              initialSizesList={sizesList}
              onChange={handleSizeChange} 
            />
          </div>
        )}

        {colorSizeList.length > 0 && (
          <div className="mt-6">
            <h3 className="text-md font-semibold text-gray-800 mb-2">Récapitulatif des couleurs, tailles et quantités</h3>
            <table className="w-full border rounded-lg overflow-hidden shadow-md">
              <thead>
                <tr className="bg-gray-200 text-gray-700">
                  <th className="p-2">Couleur</th>
                  <th className="p-2">Taille</th>
                  <th className="p-2">Quantité</th>
                </tr>
              </thead>
              <tbody>
                {colorSizeList.map(entry =>
                  entry.sizes.map(({ size, quantity }) => (
                    <tr key={`${entry.color}-${size}`} className="border-t">
                      <td className="p-2 flex items-center gap-2">
                        <span className="w-6 h-6 rounded-full border" style={{ backgroundColor: entry.color }}></span>
                        {entry.color}
                      </td>
                      <td className="p-2 text-gray-700">{size}</td>
                      <td className="p-2 w-5">
                        <Input
                          type="number"
                          min={0}
                          className="input input-bordered w-full"
                          value={quantity}
                          onChange={(e) =>
                            handleQuantityChange(entry.color, size, parseInt(e.target.value) || 0)
                          }
                        />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
});
